import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Users, Trash2, Ban, MessageCircle, MessageCircleOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api, API } from '@/api';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';

const ChatPanel = ({ gameId, isAdmin = false }) => {
  const { viewer } = useViewerAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [viewerCount, setViewerCount] = useState(0);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimer = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load initial messages
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await api.getChatMessages(gameId);
        setMessages(res.data.messages || []);
      } catch { /* ignore */ }
    };
    loadMessages();
  }, [gameId]);

  // WebSocket connection
  useEffect(() => {
    // Pass auth token in query param for server-side identity verification
    const token = localStorage.getItem('viewer_token') || '';
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/chat/${gameId}${tokenParam}`;
    
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        reconnectTimer.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'message') {
            setMessages(prev => [...prev.slice(-200), data]);
          } else if (data.type === 'viewer_count') {
            setViewerCount(data.count);
          } else if (data.type === 'delete_message') {
            setMessages(prev => prev.filter(m => m.id !== data.message_id));
          } else if (data.type === 'chat_toggled') {
            setChatEnabled(data.enabled);
          } else if (data.type === 'user_banned' && data.viewer_id === viewer?.id) {
            setMessages(prev => [...prev, { id: 'ban', type: 'system', text: 'You have been muted by a moderator.', timestamp: new Date().toISOString() }]);
          }
        } catch { /* ignore */ }
      };
    };

    connect();

    // Ping keepalive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [gameId, viewer?.id]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  const sendMessage = useCallback(() => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (!viewer) return;

    wsRef.current.send(JSON.stringify({
      type: 'message',
      viewer_id: viewer.id,
      viewer_name: viewer.name || viewer.email?.split('@')[0] || 'Anonymous',
      text: input.trim(),
      is_admin: isAdmin,
    }));
    setInput('');
  }, [input, viewer, isAdmin]);

  const deleteMessage = useCallback(async (messageId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/admin/chat/${gameId}/messages/${messageId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch { /* ignore */ }
  }, [gameId]);

  const banUser = useCallback(async (viewerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/admin/chat/${gameId}/ban/${viewerId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch { /* ignore */ }
  }, [gameId]);

  const toggleChat = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/admin/chat/${gameId}/toggle`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setChatEnabled(data.enabled);
    } catch { /* ignore */ }
  }, [gameId]);

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-xl border border-gray-700 overflow-hidden" data-testid="chat-panel">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-gray-700 flex items-center justify-between" style={{ backgroundColor: '#0F202D' }}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-sky-400" />
          <span className="text-white text-sm font-medium">Live Chat</span>
          <Badge className={`text-xs ${connected ? 'bg-green-600' : 'bg-gray-600'} text-white`}>
            {connected ? 'Connected' : 'Offline'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {viewerCount}
          </span>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={toggleChat} className="h-6 px-1.5 text-gray-400 hover:text-white">
              {chatEnabled ? <MessageCircleOff className="w-3.5 h-3.5" /> : <MessageCircle className="w-3.5 h-3.5" />}
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0" style={{ maxHeight: '400px' }}>
        {!chatEnabled && (
          <div className="text-center text-gray-500 text-sm py-4">Chat is currently disabled</div>
        )}
        {messages.map((msg, idx) => (
          <div key={msg.id || idx} className={`group ${msg.type === 'system' ? 'text-center' : ''}`}>
            {msg.type === 'system' ? (
              <span className="text-gray-500 text-xs italic">{msg.text}</span>
            ) : (
              <div className="flex items-start gap-1.5">
                <div className="flex-1 min-w-0">
                  <span className={`text-xs font-semibold ${msg.is_admin ? 'text-red-400' : 'text-sky-400'}`}>
                    {msg.viewer_name}{msg.is_admin ? ' (Admin)' : ''}
                  </span>
                  <span className="text-gray-300 text-sm ml-1.5 break-words">{msg.text}</span>
                </div>
                {isAdmin && msg.viewer_id && (
                  <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                    <button onClick={() => deleteMessage(msg.id)} className="text-gray-600 hover:text-red-400 p-0.5">
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <button onClick={() => banUser(msg.viewer_id)} className="text-gray-600 hover:text-orange-400 p-0.5">
                      <Ban className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {viewer && chatEnabled ? (
        <div className="p-2 border-t border-gray-700">
          <div className="flex gap-1.5">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type a message..."
              className="h-8 text-sm bg-gray-800 border-gray-600 text-white placeholder-gray-500"
              maxLength={500}
              data-testid="chat-input"
            />
            <Button size="sm" onClick={sendMessage} className="h-8 px-3 bg-sky-500 hover:bg-sky-600" data-testid="chat-send-btn">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : !viewer ? (
        <div className="p-3 border-t border-gray-700 text-center">
          <p className="text-gray-400 text-xs">Sign in to chat</p>
        </div>
      ) : null}
    </div>
  );
};

export default ChatPanel;
